import type {} from '@mui/x-date-pickers/AdapterDayjs';
import {
    DatePicker,
    DatePickerProps,
    DatePickerSlotProps,
    DateValidationError,
    PickerChangeHandlerContext,
    PickersActionBarAction,
} from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { FormattedMessage } from 'react-intl';
import { InputAdornment } from '@mui/material';
import { ReactElement, ReactNode, useState } from 'react';

import { PoolButton } from '../buttons';
import { PoolDatePickerActionBar } from './pool-date-picker-action-bar';
import { PoolTextfield, PoolTextfieldProps } from '../pool-textfield';

// Remove fields from slotProps that are managed by the pool-DatePicker component
export type PoolDatePickerSlotProps = Omit<
    DatePickerSlotProps<Dayjs, false>,
    'textField' | 'inputAdornment' | 'actionBar'
> & {
    textField?: Omit<PoolTextfieldProps, 'onBlur' | 'name' | 'required' | 'helperText' | 'fullWidth' | 'error'>;
};

export interface PoolDatePickerNewProps extends DatePickerProps<Dayjs> {
    name?: string;
    required?: boolean;
    helperText?: ReactNode;
    error?: boolean;
    fullWidth?: boolean;
    onBlur?: () => void;
    slotProps?: PoolDatePickerSlotProps;
}

export const PoolDatePicker = (props: PoolDatePickerNewProps): ReactElement => {
    const { name, required, slotProps, helperText, error, onBlur, fullWidth = true, ...datePickerProps } = props;
    const [pickerError, setPickerError] = useState<DateValidationError>(null);

    const handleChange = (value: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>): void => {
        setPickerError(context.validationError);
        props.onChange?.(value, context);
    };

    const getHelperText = (): ReactNode => {
        if (helperText) {
            return helperText;
        }

        if (
            pickerError &&
            pickerError !== 'minDate' &&
            pickerError !== 'maxDate' &&
            !props.disabled &&
            !props.readOnly
        ) {
            return <FormattedMessage id={'components.datePicker.' + pickerError} />;
        }
        return '';
    };

    const handleError = (error: DateValidationError, value: Dayjs | null): void => {
        props.onError?.(error, value);
        setPickerError(error);
    };

    return (
        <DatePicker
            {...datePickerProps}
            slots={{
                actionBar: PoolDatePickerActionBar,
                textField: PoolTextfield,
            }}
            slotProps={{
                ...slotProps,
                // The one symbol of a date that Tab stops at in a PALO form grid.
                openPickerButton: { ...slotProps?.openPickerButton, 'data-palo-tab-stop': true } as never,
                inputAdornment: (
                    <InputAdornment position="start">
                        <PoolButton>Submit</PoolButton>
                    </InputAdornment>
                ),
                textField: {
                    ...slotProps?.textField,
                    name,
                    required,
                    helperText: getHelperText(),
                    hint: getHelperText(),
                    fullWidth,
                    onBlur: onBlur,
                    error,
                    // We need to cast to any, because 'hint' is not part of the MUI API
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
                popper: {
                    ...slotProps?.popper,
                    placement: 'bottom-end',
                },
                actionBar: ({ wrapperVariant }) => ({
                    actions: [
                        ...(wrapperVariant === 'desktop' ? [] : ['accept' as PickersActionBarAction]),
                        ...(required ? [] : ['clear' as PickersActionBarAction]),
                    ],
                }),
            }}
            onError={handleError}
            onChange={handleChange}
        />
    );
};
